import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import z from 'zod';

import { useGetCodebaseOverviewQuery } from '@/api/api-hooks';
import { AnalyticsBranches } from '@/components/Analytics/AnalyticsBranches';
import { AnalyticsContributors } from '@/components/Analytics/AnalyticsContributors';
import { AnalyticsOverview } from '@/components/Analytics/AnalyticsOverview';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { VersionControlIconWithDefault } from '@/components/Icons/VersionControlIcon';
import { SheetTabs } from '@/components/SheetTabs/SheetTabs';
import { SheetWithHeader } from '@/components/SheetWithHeader';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

const tabTypes = ['overview', 'branches', 'contributors'] as const;
type TabType = (typeof tabTypes)[number];

const analyticsDetailsParamsSchema = z.object({
  tab: z.enum(tabTypes),
  codebaseId: z.string().uuid(),
  organizationName: z.string(),
});

const sortOrderValues = ['asc', 'desc'] as const;

const tabSearchSchema = z.object({
  bSearch: searchParam().optional(),
  bStatus: searchParam().optional(),
  bPageIndex: fallback(z.number(), 0).default(0),
  bPageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  bSortBy: fallback(z.string(), 'last_commit_date').optional(),
  bSortOrder: fallback(z.enum(sortOrderValues), 'desc').optional(),
  cSearch: searchParam().optional(),
  cPageIndex: fallback(z.number(), 0).default(0),
  cPageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  cSortBy: fallback(z.string(), 'churn_sloc').optional(),
  cSortOrder: fallback(z.enum(sortOrderValues), 'desc').optional(),
});

const analyticsRouteApi = getRouteApi('/$organizationName/_dashboard/analytics');

export const Route = createFileRoute('/$organizationName/_dashboard/analytics/$codebaseId/$tab')({
  component: AnalyticsSheetPage,
  params: { parse: analyticsDetailsParamsSchema.parse },
  validateSearch: zodValidator(tabSearchSchema),
});

function AnalyticsSheetPage() {
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const { codebaseId, tab } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = analyticsRouteApi.useSearch();

  const { data: overview, isLoading } = useGetCodebaseOverviewQuery(codebaseId, {
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    let section = '';
    switch (tab) {
      case 'overview':
        section = 'Overview';
        break;
      case 'branches':
        section = 'Branches';
        break;
      case 'contributors':
        section = 'Contributors';
        break;
    }
    document.title = `${organizationDisplayName || 'Driver'} - Git Analytics - ${section}`;
  }, [organizationDisplayName, tab]);

  return (
    <SheetWithHeader
      isOpen
      renderHeaderSkeleton={isLoading}
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: '/$organizationName/analytics',
            params: { organizationName: routeOrganizationName },
            search,
          });
        }
      }}
      title={overview?.display_name || overview?.repository_name || ''}
      description="View detailed analytics for this codebase"
      icon={<VersionControlIconWithDefault source={overview?.provider} className="h-8 w-8" />}
      widthClassName="w-[90vw] !max-w-[1000px]"
    >
      <SheetTabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            to: '/$organizationName/analytics/$codebaseId/$tab',
            params: {
              organizationName: routeOrganizationName,
              codebaseId: codebaseId,
              tab: value as TabType,
            },
            search: (prev) => prev,
          })
        }
        tabs={[
          {
            value: 'overview',
            label: 'Overview',
            content: <AnalyticsOverview codebaseId={codebaseId} />,
          },
          {
            value: 'branches',
            label: 'Branches',
            content: <AnalyticsBranches codebaseId={codebaseId} />,
          },
          {
            value: 'contributors',
            label: 'Contributors',
            content: <AnalyticsContributors codebaseId={codebaseId} />,
          },
        ]}
      />
    </SheetWithHeader>
  );
}
